import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { forkJoin } from 'rxjs';
import { CustomRouter } from 'src/admin-front/ajs-upgraded-providers';
import { AlcmeonContextService } from 'src/app/core/alcmeon-context/alcmeon-context.service';
import { SitesService } from 'src/app/mobile/shared/sites.service';
import { CustomEvent } from 'src/app/parameters/custom-events/custom-event.model';
import { RoleName } from 'src/app/parameters/users/shared/models';
import { ConfirmDialogComponent } from 'src/app/shared/components/confirm-dialog/confirm-dialog.component';
import { SiteV2 } from 'src/app/shared/models';
import { BasicService } from 'src/app/shared/services/basic.service';
import { MetricsService } from 'src/app/shared/services/metrics.service';
import { ReportsService } from '../../services/reports.service';
import { ReportEditionMetricDialogComponent } from '../../shared/components/report-edition-dialog/report-edition-metric-dialog/report-edition-metric-dialog.component';
import { ReportEditionVerbatimDialogComponent } from '../../shared/components/report-edition-dialog/report-edition-verbatim-dialog/report-edition-verbatim-dialog.component';
import { ScenarioFlavorEnum } from '../../shared/enums';
import { Report, ScenarioSimpleInfo } from '../../shared/models';

@Component({
  selector: 'app-dashboard-reports',
  templateUrl: './dashboard-reports.component.html',
  styleUrls: ['./dashboard-reports.component.scss'],
})
export class DashboardReportsComponent implements OnInit {
  @Input() roles: RoleName[];
  @Input() scenarios: ScenarioSimpleInfo[];
  @Input() customEvents: CustomEvent[];
  private stateMetrics = 'auth.alcmeon.fuse.metrics-and-reports.metrics';
  private stateVerbatim = 'auth.alcmeon.fuse.verbatim';
  displayedColumns: string[] = [
    'id',
    'name',
    'sentByEmail',
    'sentByFTP',
    'created_time',
    'createdBy',
    'actionsButtons',
  ];
  dataSource: MatTableDataSource<Report> = new MatTableDataSource([]);
  @ViewChild(MatSort) sort: MatSort;
  reports: Report[];
  isLoading = true;
  numberOfMetricReports = 0;
  numberOfVerbatimReports = 0;

  constructor(
    private alcmeonContextService: AlcmeonContextService,
    private basicService: BasicService,
    private customRouter: CustomRouter,
    private dialog: MatDialog,
    private metricsService: MetricsService,
    private reportsService: ReportsService,
    private sitesService: SitesService
  ) {}

  ngOnInit() {
    forkJoin({
      metricReports: this.reportsService.getMetricsReportsList(),
      verbatimReports: this.reportsService.getVerbatimReportsList(),
    }).subscribe(reports => {
      this.reports = reports.metricReports.concat(reports.verbatimReports);
      this.reports = this.addUIFieldsAndIdInReports(this.reports);
      this.dataSource = new MatTableDataSource(this.reports);
      this.dataSource.filterPredicate = (data, filter: string): boolean => {
        return data.name.toLowerCase().includes(filter) || `${data.id}` === filter;
      };
      this.dataSource.sort = this.sort;
      this.dataSource.sortingDataAccessor = (item, property) => {
        switch (property) {
          case 'id':
            return item[property];
          case 'sentByEmail': {
            const value = item.send_by.email
              ? this.basicService.translate('metric-reports/list/frequency/' + item.when)
              : this.basicService.translate('metric-reports/list/none');
            return value.toLowerCase();
          }
          case 'sentByFTP': {
            const value = item.send_by.ftp
              ? this.basicService.translate('metric-reports/list/frequency/' + item.when)
              : this.basicService.translate('metric-reports/list/none');
            return value.toLowerCase();
          }
          case 'createdBy': {
            return item.ui.createdBy.toLowerCase();
          }
          default: {
            return item[property].toLowerCase();
          }
        }
      };
      this.numberOfMetricReports = reports.metricReports.length;
      this.numberOfVerbatimReports = reports.verbatimReports.length;
      this.isLoading = false;
    });
  }

  /**
   * Adds UI fields:
   * - createdBy: string -> for the data that is in the Created by column
   * - isEditionDisabled: boolean -> to see when you can edit and delete reports
   * (admin can edit and delete all the reports, users only one that they created),
   * it desabled the buttons
   *
   * ReportId -> to have unique id for metric and verbatim reports when they are together
   *
   * @param reports
   * @returns reports with added fields
   */
  private addUIFieldsAndIdInReports(reports: Report[]): Report[] {
    const { is_company_admin, id } = this.alcmeonContextService.self;
    const company = this.alcmeonContextService.getCompany();
    reports.forEach((report, index) => {
      report.ui = {
        createdBy: company.usersMap.get(report.created_by).realname,
        isEditionDisabled: is_company_admin === 0 && !(report.created_by === id),
      };
      report.reportId = index + 1;
    });
    return reports;
  }

  filterByReportName(filterValue: string) {
    this.dataSource.filter = filterValue.trim().toLowerCase();
  }

  goToAnalyticsPage(report: Report) {
    if (report.metric_id) {
      this.customRouter.go(this.stateMetrics, {
        metricId: report.metric_id,
        fromReportId: report.id,
      });
    } else {
      this.customRouter.go(this.stateVerbatim);
    }
  }

  editReport(report: Report) {
    const sites: SiteV2[] = this.sitesService.sitesV2;
    if (report.metric_id) {
      this.metricsService.getMetric(report.metric_id).subscribe(metric => {
        this.dialog.open(ReportEditionMetricDialogComponent, {
          disableClose: true,
          panelClass: ['large-size'],
          data: {
            dialogTitle: this.basicService.translate('metric-reports/edition/edit-title'),
            report: report,
            sites: sites,
            scenarios: this.scenarios,
            roles: this.roles,
            customEvents: this.customEvents,
            metric: metric,
            isCreation: false,
          },
        });
      });
    } else {
      this.metricsService
        .getScenariosSimplifiedInfo(ScenarioFlavorEnum.satisfactionSurvey, true)
        .subscribe((scenarios: ScenarioSimpleInfo[]) => {
          this.dialog.open(ReportEditionVerbatimDialogComponent, {
            disableClose: true,
            panelClass: ['large-size'],
            data: {
              dialogTitle: this.basicService.translate('verbatim-reports/edition/edit-title'),
              report: report,
              sites: sites,
              scenarios: scenarios,
              isCreation: false,
            },
          });
        });
    }
  }

  deleteReport(report: Report) {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        description: this.basicService.translate('metric-reports/list/confirm-deletion'),
        confirmBtnText: this.basicService.translate('ok'),
        cancelBtnText: this.basicService.translate('cancel'),
      },
    });

    dialogRef.afterClosed().subscribe(result => {
      const deleteRow = () => {
        const reportIndex = this.reports.indexOf(report);
        this.reports.splice(reportIndex, 1);
        if (report.metric_id) {
          this.numberOfMetricReports--;
        } else {
          this.numberOfVerbatimReports--;
        }
        this.dataSource.data = this.reports;
      };
      if (result) {
        if (report.metric_id) {
          this.reportsService.deleteMetricReport(report.id).subscribe({
            next: () => {
              deleteRow();
              this.basicService.toastSuccess('metric-reports/edition/delete-success');
            },
          });
        } else {
          this.reportsService.deleteVerbatimReport(report.id).subscribe({
            next: () => {
              deleteRow();
              this.basicService.toastSuccess('verbatim-reports/edition/delete-success');
            },
          });
        }
      }
    });
  }
}
